import { datadogRum } from '@datadog/browser-rum'
import type { AnalyticsProps } from '../../../types/assertive'
import loadScriptAsync from '../../loadScriptAsync'
import debug from '../../console'
import { currentConsentState } from '../../consent/reportConsentState'
import { writeCustomDimensions } from './customDimensions'
import { packLayout } from './dimensions'
import { markAyManagerFailed } from './queue'
import { ayManagerUrl } from '../adUrls'
import { resolvePartner } from '../../../partners/resolvePartner'
const logger = debug.withTag('utils:scriptLoadAY')

export async function scriptLoadAY(
  ayEntityId: string | unknown | null,
  analyticsProps: AnalyticsProps,
  layoutName: string,
  layoutSkeleton: string,
  cohort: string | null,
  articleId: string | null,
  userId: string | null,
  isDevMode: boolean,
  devDisableAds: boolean,
  devAYTestEntityId: string | null,
  pageview_id: string | null
): Promise<boolean> {
  if (isDevMode && devDisableAds) {
    logger.info('Ads are disabled in development mode')
    return false
  }

  // The manager queue is seeded before this runs, so check for the loaded manager.
  if (window.assertive && window.ayManagerEnv?.versionInfo) {
    return true
  }

  const entityId =
    isDevMode && devAYTestEntityId ? devAYTestEntityId : ayEntityId

  // Ensure entityId is a string
  if (!entityId || typeof entityId !== 'string') {
    logger.warn(
      'Entity ID is not a valid string, cannot initialize Assertive Yield'
    )
    return false
  }

  const layout = packLayout(cohort, layoutName)
  const partner = resolvePartner() ?? null

  const analyticsOptions = {
    integrations: { webAnalytics: true },
    logUnfilled: true,

    // When adding new custom dimensions please update the docs here
    // https://redbrickmedia.atlassian.net/wiki/spaces/Duplex/pages/3809312813/AY+Custom+Dimensions
    custom: {
      userState: null, // `<engagement>[:<hemProvider>]` — built in userState.ts, written by updateUserEngagementTracking and 2.user-identity.client.ts
      layout, // `<cohort>:<splittestname>`
      custom_1: analyticsProps.type_tag,
      custom_2: analyticsProps.ntp_app_version,
      custom_3: analyticsProps.host_browser_app_version,
      custom_4: getTruncatedUrl(),
      custom_5: layoutSkeleton,
      custom_7: articleId,
      custom_8: userId, // composite `<userId>-<installDate>`
      custom_9: pageview_id,
      custom_10: null, // `<lastEngagementAction>:<daysSinceLastEngagement>:<daysSinceInstall>` — set dynamically via updateUserEngagementTracking
      custom_11: partner, // traffic partner id — partners/registry.ts
      custom_15: currentConsentState(), // consentState — patched on revision via 6.consent-state.client.ts
    },
    useHistoryChangeTrigger: false,
  }
  const assertiveOptions = {
    entityId: entityId,
    debug: false,
    analytics: analyticsOptions,
  }

  window.assertiveYieldAnalytics ??= { ...analyticsOptions }
  window.assertive ??= { ...assertiveOptions }

  try {
    if (entityId) {
      await loadScriptAsync('head', {
        src: ayManagerUrl(entityId),
      })
    }
  } catch (error) {
    logger.error('Failed to load Assertive Yield SDK:', error)
    markAyManagerFailed()
    datadogRum.setGlobalContextProperty('ay_manager_load', 'failed')
    return false
  }
  datadogRum.setGlobalContextProperty('ay_manager_load', 'loaded')

  // since we need flags to resolve we push to the queue to ensure it runs after the sdk is loaded and flags are resolved.
  // The manager also rebuilds analytics.custom from its dashboard config, which nulls custom_5–9 (and layout on
  // the Quartz entities) and drops custom_10 onward.
  writeCustomDimensions(() => ({
    layout,
    custom_1: analyticsProps.type_tag,
    custom_5: layoutSkeleton,
    custom_7: articleId,
    custom_8: userId,
    custom_9: pageview_id,
    custom_11: partner,
    custom_15: currentConsentState(),
  }))

  return true
}
